import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { TokenContext } from "../context/TokenContext";

const ShopCartContext = createContext();

export const useShopCart = () => {
	const context = useContext(ShopCartContext);
	if (!context) {
		throw new Error("useShopCart must be used within a ShopCartProvider");
	}
	return context;
};

// Helper function to generate unique cart key for product combinations
const getCartItemKey = (productId, selectedOption, selectedAddOns) => {
	const sortedAddOns =
		selectedAddOns && selectedAddOns.length > 0
			? [...selectedAddOns]
					.sort((a, b) => String(a.id).localeCompare(String(b.id)))
					.map((a) => `${a.id}:${a.quantity || 1}`)
					.join(",")
			: "";

	const optionPart = selectedOption?.id ? `_opt_${selectedOption.id}` : "";
	const addOnsPart = sortedAddOns ? `_addons_${sortedAddOns}` : "";

	return `${productId}${optionPart}${addOnsPart}`;
};

export const ShopCartProvider = ({ children }) => {
	const { appId } = useContext(TokenContext);
	const [cart, setCart] = useState([]);
	const [coupon, setCoupon] = useState(null);
	const isInitialLoad = useRef(true);

	// Memoize keys to prevent unnecessary re-renders
	const CART_KEY = useMemo(() => `${appId}_product_cart`, [appId]);
	const COUPON_KEY = useMemo(() => `${appId}_coupon`, [appId]);

	// Load cart from localStorage on mount
	useEffect(() => {
		if (!appId) return;

		try {
			const savedCart = localStorage.getItem(CART_KEY);
			if (savedCart) {
				const parsedCart = JSON.parse(savedCart);
				// Validate that it's an array
				if (Array.isArray(parsedCart)) {
					setCart(parsedCart);
				} else {
					console.warn("Invalid cart data in localStorage, clearing it");
					localStorage.removeItem(CART_KEY);
				}
			}

			const savedCoupon = localStorage.getItem(COUPON_KEY);
			if (savedCoupon) {
				try {
					const parsedCoupon = JSON.parse(savedCoupon);
					setCoupon(parsedCoupon);
				} catch (e) {
					console.error("Error parsing coupon from localStorage:", e);
					localStorage.removeItem(COUPON_KEY);
				}
			}
		} catch (error) {
			console.error("Error loading cart from localStorage:", error);
			// Clear corrupted data
			localStorage.removeItem(CART_KEY);
			localStorage.removeItem(COUPON_KEY);
		} finally {
			// Mark initial load as complete after a short delay to ensure state is set
			setTimeout(() => {
				isInitialLoad.current = false;
			}, 100);
		}
	}, [appId, CART_KEY, COUPON_KEY]);

	// Save cart to localStorage whenever it changes (but not during initial load)
	useEffect(() => {
		if (!appId || isInitialLoad.current) return;

		try {
			// Only save if cart is not empty or if we're explicitly clearing it
			const cartString = JSON.stringify(cart);
			localStorage.setItem(CART_KEY, cartString);
			// Dispatch event for other components
			window.dispatchEvent(
				new CustomEvent("shopCartUpdated", { detail: { cart } })
			);
		} catch (error) {
			console.error("Error saving cart to localStorage:", error);
			// If localStorage is full or there's an error, try to handle it gracefully
			if (error.name === "QuotaExceededError") {
				console.error("LocalStorage quota exceeded. Cart may not be saved.");
			}
		}
	}, [cart, appId, CART_KEY]);

	// Add product to cart
	const addToCart = useCallback(
		(
			productId,
			quantity,
			selectedOption,
			stock,
			selectedAddOns = null,
			productData = null
		) => {
			setCart((prevCart) => {
				const cartKey = getCartItemKey(
					productId,
					selectedOption,
					selectedAddOns
				);

				// Find existing item with same key
				const existingIndex = prevCart.findIndex((item) => {
					const itemKey = getCartItemKey(
						item.productId,
						item.selectedOption,
						item.selectedAddOns || []
					);
					return itemKey === cartKey;
				});

				if (existingIndex !== -1) {
					// Update existing item
					const newCart = [...prevCart];
					newCart[existingIndex] = {
						...newCart[existingIndex],
						quantity: newCart[existingIndex].quantity + quantity,
					};
					return newCart;
				} else {
					// Add new item
					return [
						...prevCart,
						{
							productId,
							quantity,
							selectedOption,
							selectedAddOns: selectedAddOns || [],
							stock,
							productData, // Store product data for display
						},
					];
				}
			});

			return true;
		},
		[]
	);

	// Update product quantity in cart
	const updateCartItem = useCallback(
		(productId, quantity, selectedOption, selectedAddOns = null) => {
			setCart((prevCart) => {
				const cartKey = getCartItemKey(
					productId,
					selectedOption,
					selectedAddOns || []
				);

				return prevCart.map((item) => {
					const itemKey = getCartItemKey(
						item.productId,
						item.selectedOption,
						item.selectedAddOns || []
					);
					if (itemKey === cartKey) {
						return { ...item, quantity };
					}
					return item;
				});
			});
		},
		[]
	);

	// Remove product from cart
	const removeFromCart = useCallback(
		(productId, selectedOption, selectedAddOns = null) => {
			setCart((prevCart) => {
				const cartKey = getCartItemKey(
					productId,
					selectedOption,
					selectedAddOns || []
				);

				return prevCart.filter((item) => {
					const itemKey = getCartItemKey(
						item.productId,
						item.selectedOption,
						item.selectedAddOns || []
					);
					return itemKey !== cartKey;
				});
			});
		},
		[]
	);

	// Clear entire cart
	const clearCart = useCallback(() => {
		setCart([]);
		setCoupon(null);
		if (appId) {
			try {
				localStorage.removeItem(CART_KEY);
				localStorage.removeItem(COUPON_KEY);
				// Dispatch event to notify other components
				window.dispatchEvent(
					new CustomEvent("shopCartUpdated", { detail: { cart: [] } })
				);
			} catch (error) {
				console.error("Error clearing cart from localStorage:", error);
			}
		}
	}, [appId, CART_KEY, COUPON_KEY]);

	// Get cart items count
	const getCartItemsCount = useCallback(() => {
		return cart.reduce((sum, item) => sum + item.quantity, 0);
	}, [cart]);

	// Calculate cart total (without shipping)
	const calculateCartTotal = useCallback(
		(products) => {
			return cart.reduce((total, cartItem) => {
				const product =
					products?.find((p) => p.id === cartItem.productId) ||
					cartItem.productData;
				if (!product) return total;

				let itemPrice = product.price || 0;

				// Apply product discount
				if (product.discount) {
					itemPrice = itemPrice - (itemPrice * product.discount) / 100;
				}

				// Use option price if selected
				if (cartItem.selectedOption) {
					itemPrice = cartItem.selectedOption.price || itemPrice;
					if (cartItem.selectedOption.discount) {
						itemPrice =
							itemPrice - (itemPrice * cartItem.selectedOption.discount) / 100;
					}
				}

				// Add add-ons prices
				if (
					cartItem.selectedAddOns &&
					cartItem.selectedAddOns.length > 0 &&
					product.addOnGroups
				) {
					cartItem.selectedAddOns.forEach((selected) => {
						const addOn = product.addOnGroups
							.flatMap((g) => g?.addOns || [])
							.find((a) => a?.id === selected.id);
						if (addOn) {
							let addOnPrice = addOn.price || 0;
							if (addOn.discount) {
								addOnPrice = addOnPrice - (addOnPrice * addOn.discount) / 100;
							}
							itemPrice += addOnPrice * (selected.quantity || 1);
						}
					});
				}

				return total + itemPrice * cartItem.quantity;
			}, 0);
		},
		[cart]
	);

	// Save coupon to localStorage whenever it changes (but not during initial load)
	useEffect(() => {
		if (!appId || isInitialLoad.current) return;

		try {
			if (coupon) {
				localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
			} else {
				localStorage.removeItem(COUPON_KEY);
			}
		} catch (error) {
			console.error("Error saving coupon to localStorage:", error);
		}
	}, [coupon, appId, COUPON_KEY]);

	// Set coupon
	const addCoupon = useCallback((couponData) => {
		setCoupon(couponData);
	}, []);

	// Remove coupon
	const removeCoupon = useCallback(() => {
		setCoupon(null);
	}, []);

	// Calculate total weight of cart in kg
	const calculateCartWeight = useCallback(
		(products) => {
			return cart.reduce((totalWeight, cartItem) => {
				const product =
					products?.find((p) => p.id === cartItem.productId) ||
					cartItem.productData;
				if (!product || !product.weightValue) return totalWeight;

				let weightInKg = product.weightValue;
				switch (product.weightUnit) {
					case "g":
						weightInKg = product.weightValue / 1000;
						break;
					case "kg":
						weightInKg = product.weightValue;
						break;
					case "ml":
						weightInKg = product.weightValue / 1000;
						break;
					case "L":
						weightInKg = product.weightValue;
						break;
					default:
						weightInKg = product.weightValue;
				}

				return totalWeight + weightInKg * cartItem.quantity;
			}, 0);
		},
		[cart]
	);

	const value = {
		cart,
		coupon,
		addToCart,
		updateCartItem,
		removeFromCart,
		clearCart,
		getCartItemsCount,
		calculateCartTotal,
		calculateCartWeight,
		addCoupon,
		removeCoupon,
	};

	return (
		<ShopCartContext.Provider value={value}>
			{children}
		</ShopCartContext.Provider>
	);
};

export default ShopCartContext;
